import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TextField,
  Paper,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Fade,
  Slide,
  IconButton,
  LinearProgress,
  Alert,
  Collapse,
  Button,
  ButtonGroup,
  Divider,
  Tooltip,
  Zoom,
  CircularProgress,
} from "@mui/material";
import {
  AttachMoney as CoinIcon,
  AccountBalance as WalletIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  SwapHoriz as TransferIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useUser } from "../authMethods/UserContext";
import ChessButton from "./ChessButton";
import { useTranslation } from "react-i18next";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Swal from "sweetalert2";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";

const SendCoins = ({ preselectedUser = null, onClose }) => {
  const { userData } = useUser();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [transferComplete, setTransferComplete] = useState(false);
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    selectedUserForTransfer: preselectedUser || "",
    coinsToTransfer: "",
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [quickAmounts] = useState([10, 25, 50, 100, 250, 500]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentCoins = userData?.userStatistics?.coins ?? 0;
  const selectedUser = settings.selectedUserForTransfer;
  const transferAmount = Number(settings.coinsToTransfer);

  useEffect(() => {
    const handleWebSocketEvent = (event) => {
      if (
        event.type === "message" &&
        event.message.messageType === "favorites_data"
      ) {
        setFavorites(event.message.favoriteStatistics || []);
        setLoading(false);
      }
    };

    addListener(handleWebSocketEvent);
    requestForFavorites();

    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, []);

  const requestForFavorites = () => {
    const generalMessage = {
      messageType: "get_favorites",
    };
    sendMessage(generalMessage);
  };

  useEffect(() => {
    if (preselectedUser && favorites) {
      const matchedUser = favorites.find(
        (u) => u.userName === preselectedUser.userName
      );
      if (matchedUser) {
        setSettings((prev) => ({
          ...prev,
          selectedUserForTransfer: matchedUser,
        }));
      }
    }
  }, [preselectedUser, favorites]);

  const handleInputChange = (event) => {
    const value = Number(event.target.value);
    
    if (value < 0) return;
    if (value > currentCoins) {
      setSettings((prev) => ({ ...prev, coinsToTransfer: currentCoins }));
      return;
    }
    
    setSettings((prev) => ({ ...prev, coinsToTransfer: value }));
  };

  const handleQuickAmount = (amount) => {
    if (amount > currentCoins) {
      Swal.fire({
        title: t("Not enough coins"),
        text: t("You don't have enough coins for this amount"),
        icon: "warning",
        confirmButtonText: t("OK"),
      });
      return;
    }
    setSettings((prev) => ({ ...prev, coinsToTransfer: amount }));
  };

  const handleTransfer = async () => {
    if (!selectedUser || !transferAmount) return;

    setTransferring(true);
    
    // Simulate transfer delay
    setTimeout(() => {
      const message = {
        ...generalMessageTemplate,
        messageType: "transfer_coins",
        user: {
          userName: selectedUser.userName,
          coinsToTransfer: transferAmount,
        },
      };

      sendMessage(message);
      setTransferring(false);
      setTransferComplete(true);
      setShowConfirmation(false);
      
      // Reset after showing success
      setTimeout(() => {
        setSettings({ selectedUserForTransfer: "", coinsToTransfer: "" });
        setTransferComplete(false);
      }, 3000);
    }, 2000);
  };

  const isFormValid = () => {
    return selectedUser && transferAmount > 0 && transferAmount <= currentCoins;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (transferComplete) {
    return (
      <Zoom in>
        <Card
          sx={{
            maxWidth: 500,
            mx: "auto",
            background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
            color: "white",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Close Button for Success Screen */}
          {onClose && (
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <CardContent sx={{ p: 4 }}>
            <CheckIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" mb={1}>
              {t("Transfer Complete!")}
            </Typography>
            <Typography variant="body1">
              {t("Successfully sent")} {transferAmount} {t("coins to")} {selectedUser?.socialUser?.displayName}
            </Typography>
          </CardContent>
        </Card>
      </Zoom>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        p: isMobile ? 1 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9998,
        position: "relative",
      }}
    >
      {/* Close Button */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10000,
            backgroundColor: "rgba(255,255,255,0.9)",
            color: "#1a1a1a",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <Card
        sx={{
          maxWidth: 600,
          width: "100%",
          borderRadius: 4,
          overflow: "hidden",
        }}
        elevation={24}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(45deg, #1a1a1a 30%, #2d2d2d 90%)",
            color: "white",
            p: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                backgroundColor: "#ffd700",
                color: "#1a1a1a",
                width: 48,
                height: 48,
              }}
            >
              <CoinIcon />
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">
                {t("Send Coins")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {t("Transfer coins to your friends")}
              </Typography>
            </Box>

            <Chip
              icon={<WalletIcon />}
              label={`${currentCoins} ${t("coins")}`}
              sx={{
                backgroundColor: "rgba(255,215,0,0.2)",
                color: "#ffd700",
                fontWeight: "bold",
              }}
            />
          </Box>
        </Paper>

        <CardContent sx={{ p: 3 }}>
          {/* Current Balance Card */}
          <Fade in timeout={600}>
            <Card
              sx={{
                mb: 3,
                background: "linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)",
                color: "#1a1a1a",
              }}
            >
              <CardContent sx={{ p: 2, textAlign: "center" }}>
                <WalletIcon sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {t("Your Balance")}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {currentCoins.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  {t("coins available")}
                </Typography>
              </CardContent>
            </Card>
          </Fade>

          {/* Select Friend */}
          <Slide direction="up" in timeout={800}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                {t("Select Friend")}
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>{t("Choose recipient")}</InputLabel>
                <Select
                  value={selectedUser?.userName || ""}
                  label={t("Choose recipient")}
                  onChange={(event) => {
                    const selectedUserName = event.target.value;
                    const foundUser = favorites?.find(
                      (user) => user.userName === selectedUserName
                    );
                    setSettings((prev) => ({
                      ...prev,
                      selectedUserForTransfer: foundUser || null,
                    }));
                  }}
                  renderValue={(value) => {
                    const user = favorites.find(f => f.userName === value);
                    return user ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24 }} />
                        {user.socialUser?.displayName}
                        {user.online && (
                          <Chip 
                            label={t("Online")} 
                            size="small" 
                            color="success"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    ) : "";
                  }}
                >
                  {favorites?.map((favUser, index) => (
                    <MenuItem key={index} value={favUser.userName}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                        <Avatar sx={{ width: 32, height: 32 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">
                            {favUser.socialUser?.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{favUser.userName}
                          </Typography>
                        </Box>
                        {favUser.online && (
                          <Chip 
                            label={t("Online")} 
                            size="small" 
                            color="success"
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Slide>

          {/* Amount Selection */}
          <Slide direction="up" in timeout={1000}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                <CoinIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                {t("Amount")}
              </Typography>

              {/* Quick Amount Buttons */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {t("Quick amounts")}:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {quickAmounts.map((amount) => (
                    <Tooltip 
                      key={amount} 
                      title={amount > currentCoins ? t("Not enough coins") : ""}
                    >
                      <span>
                        <Button
                          variant={transferAmount === amount ? "contained" : "outlined"}
                          size="small"
                          onClick={() => handleQuickAmount(amount)}
                          disabled={amount > currentCoins}
                          sx={{
                            minWidth: 60,
                            borderRadius: 3,
                            textTransform: "none",
                          }}
                        >
                          {amount}
                        </Button>
                      </span>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>

              {/* Custom Amount Input */}
              <TextField
                fullWidth
                type="number"
                label={t("Custom amount")}
                value={settings.coinsToTransfer}
                onChange={handleInputChange}
                InputProps={{
                  inputProps: {
                    min: 1,
                    max: currentCoins,
                  },
                  endAdornment: (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleInputChange({ 
                          target: { value: Math.max(0, transferAmount - 1) } 
                        })}
                        disabled={transferAmount <= 1}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleInputChange({ 
                          target: { value: Math.min(currentCoins, transferAmount + 1) } 
                        })}
                        disabled={transferAmount >= currentCoins}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ),
                }}
                helperText={
                  transferAmount > currentCoins 
                    ? t("Amount exceeds your balance")
                    : `${t("Available:")} ${currentCoins} ${t("coins")}`
                }
                error={transferAmount > currentCoins}
              />

              {/* Transfer Preview */}
              {transferAmount > 0 && selectedUser && (
                <Fade in>
                  <Card
                    sx={{
                      mt: 2,
                      backgroundColor: "primary.light",
                      color: "primary.contrastText",
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="body2" mb={1}>
                        {t("Transfer Preview")}:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6">
                          {transferAmount} {t("coins")}
                        </Typography>
                        <TransferIcon />
                        <Typography variant="body1">
                          {selectedUser.socialUser?.displayName}
                        </Typography>
                      </Box>
                      <Typography variant="caption">
                        {t("Remaining balance")}: {currentCoins - transferAmount} {t("coins")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              )}
            </Box>
          </Slide>

          {/* Action Buttons */}
          <Slide direction="up" in timeout={1200}>
            <Box>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => setShowConfirmation(true)}
                disabled={!isFormValid() || transferring}
                startIcon={transferring ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  background: "linear-gradient(45deg, #4caf50 30%, #45a049 90%)",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                }}
              >
                {transferring ? t("Processing...") : t("Send Coins")}
              </Button>

              {!isFormValid() && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {!selectedUser && t("Please select a friend")}
                  {selectedUser && !transferAmount && t("Please enter an amount")}
                  {selectedUser && transferAmount > currentCoins && t("Insufficient coins")}
                </Alert>
              )}
            </Box>
          </Slide>
        </CardContent>

        {/* Confirmation Dialog */}
        <Collapse in={showConfirmation}>
          <Divider />
          <Box sx={{ p: 3, backgroundColor: "warning.light" }}>
            <Typography variant="h6" mb={2} textAlign="center">
              {t("Confirm Transfer")}
            </Typography>
            
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body1" mb={1}>
                  {t("You are about to send")}:
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {transferAmount} {t("coins")}
                </Typography>
                <Typography variant="body1" mb={1}>
                  {t("to_1")}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {selectedUser?.socialUser?.displayName}
                </Typography>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowConfirmation(false)}
                sx={{ borderRadius: 3 }}
              >
                {t("Cancel")}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleTransfer}
                disabled={transferring}
                sx={{
                  borderRadius: 3,
                  background: "linear-gradient(45deg, #f44336 30%, #d32f2f 90%)",
                }}
              >
                {transferring ? t("Sending...") : t("Confirm Transfer")}
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Card>
    </Box>
  );
};

export default SendCoins;